/*
 * Rebuy MOQ (custom.moq metafield)
 * Replaces the Rebuy quantity select with MOQ multiples (moq, 2*moq, ...) on products
 * that have custom.moq, and removes products/variants that can't fulfil the MOQ.
 * Runs on the `rebuy.ready` event, plus any widgets that were already ready on load.
 */
(function () {
    const MAX_STEPS = 10; // how many options to show (moq x1 ... moq x10)
    const widgetState = new Map(); // widget id -> { moqById }

    // custom.moq comes back as a string -> parse to integer (0 = no MOQ)
    const getMoq = (product) => {
        const moq = parseInt(product?.metafields?.custom?.moq, 10);
        return Number.isFinite(moq) && moq > 1 ? moq : 0;
    };

    // Same rule as the PDP: policy 'deny' + tracked inventory below MOQ = can't be sold
    const isTracked = (variant) => variant && variant.inventory_management && variant.inventory_policy === 'deny';
    const canFulfil = (variant, moq) => !isTracked(variant) || Number(variant.inventory_quantity) >= moq;

    // Options: moq, 2*moq, ... capped by available stock of the selected variant
    const buildOptions = ({ product, moq }) => {
        const variant = product.selected_variant;
        const max = isTracked(variant) ? Number(variant.inventory_quantity) : Infinity;
        const values = [];
        for (let step = 1; step <= MAX_STEPS && moq * step <= max; step++) values.push(moq * step);
        return values;
    };

    const injectSelects = (container, moqById) => {
        container.querySelectorAll('.rebuy-product-block').forEach((block) => {
            const idClass = [...block.classList].find((c) => c.startsWith('product-id-'));
            const entry = idClass && moqById.get(idClass.replace('product-id-', ''));
            if (!entry) return;

            const wrapper = block.querySelector('.product-quantity .rebuy-select-wrapper');
            if (!wrapper) return;

            const values = buildOptions(entry);
            let select = wrapper.querySelector('.rebuy-moq-select');

            if (!select) {
                select = document.createElement('select');
                select.className = 'rebuy-select rebuy-moq-select';
                select.setAttribute('aria-label', 'product quantity');
                select.dataset.productId = String(entry.product.id);
                wrapper.appendChild(select);
            }

            // Rebuild options only when they changed (e.g. variant switch)
            const signature = values.join(',');
            if (select.dataset.signature !== signature) {
                select.innerHTML = values.map((v) => `<option value="${v}">${v}</option>`).join('');
                select.dataset.signature = signature;
            }

            const current = Number(entry.product.quantity);
            if (!values.includes(current)) entry.product.quantity = values[0];
            select.value = String(entry.product.quantity);
        });
    };

    const applyRebuyMoq = (widget) => {
        const products = widget?.data?.products;
        const widgetId = widget?.data?.id;
        if (!Array.isArray(products) || !widgetId) return;

        const moqById = new Map(); // product.id -> { product, moq }

        // Loop backwards so splice doesn't shift indexes
        for (let i = products.length - 1; i >= 0; i--) {
            const product = products[i];
            const moq = getMoq(product);
            if (!moq) continue;

            const validVariants = (product.variants || []).filter((v) => canFulfil(v, moq));

            // No variant has enough stock for the MOQ -> remove product from the widget
            if (!validVariants.length) {
                products.splice(i, 1);
                continue;
            }

            product.variants = validVariants;
            if (!canFulfil(product.selected_variant, moq)) {
                product.selected_variant = validVariants[0];
                product.selected_variant_id = validVariants[0].id;
            }

            // Keep the quantity if it's already a valid multiple (e.g. re-run on refresh)
            const qty = Number(product.quantity);
            if (!(qty >= moq && qty % moq === 0)) product.quantity = moq;
            moqById.set(String(product.id), { product, moq });
        }

        if (!moqById.size) return;

        const container = document.querySelector(`[data-rebuy-id="${widgetId}"]`);
        if (!container) return;

        let state = widgetState.get(widgetId);

        // Bind listeners/observer once per widget, re-runs only refresh the product map
        if (!state) {
            state = { moqById };
            widgetState.set(widgetId, state);

            // Hide Rebuy's own quantity select only inside MOQ blocks we handle
            container.classList.add('rebuy-moq-widget');

            // Delegated listener (also works for carousel clone slides)
            container.addEventListener('change', (e) => {
                const select = e.target.closest('.rebuy-moq-select');
                if (!select) return;
                const entry = state.moqById.get(select.dataset.productId);
                if (entry) entry.product.quantity = Number(select.value);
            });

            // Re-inject whenever Rebuy re-renders (Vue updates, carousel, variant change)
            let scheduled = false;
            new MutationObserver(() => {
                if (scheduled) return;
                scheduled = true;
                requestAnimationFrame(() => {
                    scheduled = false;
                    injectSelects(container, state.moqById);
                });
            }).observe(container, { childList: true, subtree: true });
        } else {
            state.moqById = moqById;
        }

        injectSelects(container, state.moqById);
    };

    // Hide the original select wherever our MOQ select has been injected
    const style = document.createElement('style');
    style.textContent = '.rebuy-moq-widget .rebuy-select-wrapper:has(.rebuy-moq-select) select.rebuy-select:not(.rebuy-moq-select){display:none !important;}';
    document.head.appendChild(style);

    const onWidgetEvent = (event) => applyRebuyMoq(event?.detail?.widget);
    document.addEventListener('rebuy.ready', onWidgetEvent);
    document.addEventListener('rebuy.productsChange', onWidgetEvent);

    // Widgets that were already ready before this script loaded
    (window.Rebuy?.widgets || []).forEach((widget) => {
        if (widget?.data?.products?.length) applyRebuyMoq(widget);
    });
})();
